import { useCallback, useEffect, useState } from "react";
import type { MessageAttachmentDto } from "@multi-chat/shared";
import { createMessageAttachmentDownloadUrl } from "../../api/message-attachments-api.js";
import {
  clearCachedStoredAttachmentUrl,
  isPresignedUrlExpired,
  readCachedStoredAttachmentUrl,
  writeCachedStoredAttachmentUrl,
} from "./stored-attachment-url-cache.js";

export type StoredAttachmentUrlState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; url: string; expiresAt: string }
  | { status: "error" };

export type UseStoredAttachmentUrlResult = StoredAttachmentUrlState & {
  /** Forces a new download-url request and updates the cache. */
  refreshUrl: () => Promise<string | null>;
  /** Returns a valid URL, refreshing when expired or near expiry. */
  ensureUrl: () => Promise<string | null>;
};

export function useStoredAttachmentUrl(
  organizationId: number,
  attachment: MessageAttachmentDto,
): UseStoredAttachmentUrlResult {
  const [state, setState] = useState<StoredAttachmentUrlState>({ status: "idle" });
  const attachmentId = attachment.attachmentId;

  const fetchUrl = useCallback(
    async (force: boolean, isCancelled?: () => boolean): Promise<string | null> => {
      if (!force) {
        const cached = readCachedStoredAttachmentUrl(organizationId, attachmentId);
        if (cached) {
          const expiresAt = new Date(cached.expiresAtMs).toISOString();
          if (!isCancelled?.()) {
            setState({ status: "ready", url: cached.url, expiresAt });
          }
          return cached.url;
        }
      } else {
        clearCachedStoredAttachmentUrl(organizationId, attachmentId);
      }

      if (!isCancelled?.()) {
        setState({ status: "loading" });
      }

      try {
        const response = await createMessageAttachmentDownloadUrl(
          organizationId,
          attachmentId,
        );
        if (isCancelled?.()) {
          return null;
        }
        writeCachedStoredAttachmentUrl(
          organizationId,
          attachmentId,
          response.url,
          response.expiresAt,
        );
        setState({
          status: "ready",
          url: response.url,
          expiresAt: response.expiresAt,
        });
        return response.url;
      } catch {
        if (!isCancelled?.()) {
          setState({ status: "error" });
        }
        return null;
      }
    },
    [organizationId, attachmentId],
  );

  useEffect(() => {
    if (attachment.storageState !== "STORED") {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    void fetchUrl(false, () => cancelled);

    return () => {
      cancelled = true;
    };
  }, [attachment.storageState, fetchUrl]);

  const refreshUrl = useCallback(() => fetchUrl(true), [fetchUrl]);

  const ensureUrl = useCallback(async (): Promise<string | null> => {
    const cached = readCachedStoredAttachmentUrl(organizationId, attachmentId);
    if (cached) {
      const expiresAt = new Date(cached.expiresAtMs).toISOString();
      if (state.status !== "ready" || state.url !== cached.url) {
        setState({ status: "ready", url: cached.url, expiresAt });
      }
      return cached.url;
    }

    if (
      state.status === "ready" &&
      state.url &&
      state.expiresAt &&
      !isPresignedUrlExpired(state.expiresAt)
    ) {
      return state.url;
    }

    return fetchUrl(true);
  }, [organizationId, attachmentId, state, fetchUrl]);

  return {
    ...state,
    refreshUrl,
    ensureUrl,
  };
}
